import { Box, chakra, Flex, HStack, Spacer, Text } from '@chakra-ui/react';
import { useLingui } from '@lingui/react/macro';
import * as Sentry from '@sentry/react';
import { useLocation, useRouterState } from '@tanstack/react-router';
import MessageSquarePlusIcon from '~icons/lucide/message-square-plus';
import { Suspense } from 'react';

import { useIsAuth } from '#modules/user/features/auth/hooks/useIsAuth';
import { tracking } from '#features/analytics';
import { Link } from '#features/routing';
import { Button } from '#features/ui';
import { IconButton } from '#features/ui/components/IconButton';
import { envConfig } from '#constants/envConfig';
import { useSSR } from '#hooks/useSSR';
import { lazyNamedWithRetry } from '#utils/lazyWithRetry';
import { homeRoute } from '../../utils/homeRoute';
import { UserSettings } from '../UserSettings';
import { Logo } from './Logo';

const DynamicNotificationsPopover = lazyNamedWithRetry(
  () => import('#features/notifications/components/NotificationsPopover'),
  'NotificationsPopover'
);

// Заглушка держит место колокольчика, пока он грузится, и остаётся вместо него, если чанк так и
// не приехал (блокировщик, оборванная сеть): без своей границы такая ошибка всплывает до
// роутового boundary и вместо страницы показывается экран ошибки.
const notificationsPlaceholder = <Box h="40px" w="40px" />;

export const Header = () => {
  const { isBrowser } = useSSR();
  const { t } = useLingui();
  const { pathname } = useLocation();
  const isAuth = useIsAuth();
  // Landing/marketing pages flag themselves via `staticData.rendersOwnFooter` (same signal
  // MainLayout uses to suppress the global footer). On those pages, logged-out visitors get
  // marketing nav + a primary CTA; the dashboard/auth header is left untouched.
  const isLanding = useRouterState({
    select: (state) => state.matches?.some((match) => match?.staticData?.rendersOwnFooter) ?? false,
  });
  const showMarketingNav = isBrowser && !isAuth && isLanding;

  return (
    <Flex
      as="header"
      // Grows by the notch inset so the bar's own background fills the status-bar strip
      // and the logo/actions still sit in the 60px below it. Resolves to plain 60px
      // everywhere the inset is 0 — mobile web, desktop and the Android shell — so this
      // is a no-op outside the iOS app, which is the only place the webview draws
      // edge-to-edge (see useNativeViewportFit).
      h="calc(60px + env(safe-area-inset-top, 0px))"
      pt="env(safe-area-inset-top, 0px)"
      w="100%"
      pos="sticky"
      top="0"
      left="0"
      zIndex="sticky"
      position="sticky"
      bg={{ base: 'hsla(228, 11.6%, 8%, 0.78)', md: 'brand.900' }}
      backdropFilter={{ base: 'blur(14px) saturate(130%)', md: 'none' }}
      WebkitBackdropFilter={{ base: 'blur(14px) saturate(130%)', md: 'none' }}
      boxShadow="md"
    >
      <Flex maxW="1800px" px={{ base: 3, md: 5 }} py={2} w="100%" flex={1} m="0 auto">
        <Flex alignItems="center">
          <Flex px={2} w={{ base: '200px', md: 'auto' }} asChild>
            <Link to={homeRoute(isAuth)}>
              <Logo />
              <Text as="sup" mt={4} fontWeight="700" color="fg.emphasized">
                {envConfig.DEV_BADGE ? 'dev' : 'beta'}
              </Text>
            </Link>
          </Flex>
          {showMarketingNav && (
            <HStack as="nav" gap={6} ml={{ lg: 6, xl: 10 }} display={{ base: 'none', lg: 'flex' }}>
              <chakra.a
                href="#how-it-works"
                textStyle="title"
                color="fg.muted"
                _hover={{ color: 'fg.emphasized' }}
                transition="color 0.15s"
              >
                {t`How it works`}
              </chakra.a>
              <Text
                asChild
                textStyle="title"
                color="fg.muted"
                _hover={{ color: 'fg.emphasized' }}
                transition="color 0.15s"
              >
                <Link to="/{-$locale}/blog">{t`Blog`}</Link>
              </Text>
              <Text
                asChild
                textStyle="title"
                color="fg.muted"
                _hover={{ color: 'fg.emphasized' }}
                transition="color 0.15s"
              >
                <Link to="/{-$locale}/faq">{t`FAQ`}</Link>
              </Text>
            </HStack>
          )}
        </Flex>
        <Spacer />
        {isBrowser && !isAuth && (
          <HStack gap={2}>
            {isLanding && (
              <Button
                display={{ base: 'none', sm: 'flex' }}
                onClick={() =>
                  tracking.event({
                    action: 'click_sign_up',
                    category: 'auth',
                  })
                }
                asChild
              >
                <Link to="/{-$locale}/sign-up">{t`Find teammates`}</Link>
              </Button>
            )}
            {!['/sign-in'].includes(pathname) && (
              <Button
                display={{ base: 'none', sm: 'flex' }}
                onClick={() =>
                  tracking.event({
                    action: 'click_sign_in',
                    category: 'auth',
                  })
                }
                asChild
                variant="outline"
              >
                <Link to="/{-$locale}/sign-in">{t`common:auth.signIn`}</Link>
              </Button>
            )}
          </HStack>
        )}
        {isBrowser && isAuth ? (
          <HStack gap={3}>
            <Button
              display={{ base: 'none', md: 'flex' }}
              variant="outline"
              onClick={() =>
                tracking.event({
                  action: 'click_create_post',
                  category: 'posts',
                })
              }
              asChild
            >
              <Link to="/{-$locale}/dashboard/discover/create">{t`Find teammates`}</Link>
            </Button>
            {/* Постоянный вход в обратную связь: одна иконка рядом с колокольчиком вместо
                плавающей кнопки поверх контента — та на телефоне перекрывает ленту и читается
                как чужой виджет. Отсюда видно на любом экране внутри аккаунта. */}
            <IconButton
              aria-label={t`What should we improve?`}
              title={t`What should we improve?`}
              variant="ghost"
              asChild
            >
              <Link to="/{-$locale}/feedback">
                <MessageSquarePlusIcon />
              </Link>
            </IconButton>
            <Sentry.ErrorBoundary fallback={notificationsPlaceholder}>
              <Suspense fallback={notificationsPlaceholder}>
                <DynamicNotificationsPopover />
              </Suspense>
            </Sentry.ErrorBoundary>
            <UserSettings />
          </HStack>
        ) : null}
      </Flex>
    </Flex>
  );
};
